import { Controller } from '@hotwired/stimulus'

// Reading-position tracking for the chapter reader. Watches scroll (rAF +
// min-interval throttled) and derives two things from the prose container:
//   percent     — bottom-edge progress through the prose (0-100)
//   block index — the topmost visible top-level block, which is stable across
//                 font sizes/viewports so a restore lands on the same paragraph
//
// Signed-in readers: saved to the server (POST fetch keepalive) every 15s while
// the position changes, plus on tab-hide/pagehide/disconnect. Guests: saved to
// localStorage under one key per story (pruned to the newest 20 stories).
//
// When the page opens with a saved mid-chapter position (5-95%), a floating
// "Resume where you left off" pill appears; clicking it smooth-scrolls to the
// saved block (percent fallback). It hides once the reader reaches that point
// on their own, resumes, or dismisses. It never auto-scrolls — deep links and
// back-button behaviour stay intact.
//
// A chapter that fits the viewport can't be measured by scroll, so it counts
// as fully read only after an 8s dwell (mirrors the analytics controller).
const STORE_PREFIX = 'dh:read:v1:'
const MAX_GUEST_ENTRIES = 20
const SAVE_INTERVAL_MS = 15000
const MEASURE_MIN_INTERVAL_MS = 400
const SHORT_CHAPTER_DWELL_MS = 8000
const TOP_BAR_OFFSET = 72

export default class extends Controller {
  static targets = ['prose', 'pill']
  static values = {
    url: String,
    storyId: String,
    chapterId: String,
    chapterPath: String,
    chapterPosition: Number,
    signedIn: Boolean,
    resumePercent: { type: Number, default: 0 },
    resumeBlock: { type: Number, default: -1 }
  }

  connect() {
    if (!this.hasProseTarget) return
    this.blocks = Array.from(this.proseTarget.children)
    this.percent = 0
    this.blockIndex = null
    this.dirty = false
    this.lastMeasureAt = 0
    this.lastSavedKey = null
    // Nothing persists until the reader actually engages (scrolls, resumes,
    // or dwells on a short chapter). Without this, merely OPENING a page
    // would save a position — and a short final chapter, whose prose already
    // fits the first viewport, would hit >=90% and "complete" the story on
    // sight. Opening is not reading.
    this.interacted = false

    this.saved = this.savedPosition()
    if (this.saved && this.saved.pct >= 5 && this.saved.pct <= 95) {
      this.showPill()
    } else {
      // A Turbo-cached snapshot can restore the pill visible — force it away
      // when this load has nothing to resume.
      this.hidePill()
    }

    this.boundScroll = () => {
      this.interacted = true
      this.scheduleMeasure()
    }
    this.boundHide = () => { if (document.visibilityState === 'hidden') this.persist() }
    this.boundPersist = () => this.persist()
    window.addEventListener('scroll', this.boundScroll, { passive: true })
    document.addEventListener('visibilitychange', this.boundHide)
    window.addEventListener('pagehide', this.boundPersist)
    if (this.signedInValue) {
      this.saveTimer = setInterval(() => this.persist(), SAVE_INTERVAL_MS)
    }

    // A chapter whose prose already fits the first viewport can't be measured
    // by scroll (the page may still scroll for comments/footer): count it
    // fully read only after a dwell, never on sight.
    if (this.proseTarget.getBoundingClientRect().bottom <= window.innerHeight) {
      this.dwellTimer = setTimeout(() => {
        this.interacted = true
        this.percent = 100
        this.blockIndex = null
        this.dirty = true
        if (!this.signedInValue && this.shouldPersist()) this.writeGuestEntry()
      }, SHORT_CHAPTER_DWELL_MS)
    }

    this.pruneGuestEntries()
    this.scheduleMeasure()
  }

  disconnect() {
    window.removeEventListener('scroll', this.boundScroll)
    document.removeEventListener('visibilitychange', this.boundHide)
    window.removeEventListener('pagehide', this.boundPersist)
    if (this.saveTimer) clearInterval(this.saveTimer)
    if (this.dwellTimer) clearTimeout(this.dwellTimer)
    if (this.pendingMeasure) clearTimeout(this.pendingMeasure)
    // Turbo navigation never fires pagehide — flush the last position here.
    this.persist()
  }

  // ── Pill actions ─────────────────────────────────────────────────────────

  resume() {
    const saved = this.saved
    if (saved) {
      const block = Number.isInteger(saved.block) ? this.blocks[saved.block] : null
      let top
      if (block) {
        top = block.getBoundingClientRect().top + window.scrollY - TOP_BAR_OFFSET - 8
      } else {
        const rect = this.proseTarget.getBoundingClientRect()
        const proseTop = rect.top + window.scrollY
        top = proseTop + (rect.height * saved.pct) / 100 - window.innerHeight / 3
      }
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' })
    }
    this.hidePill()
  }

  dismiss() {
    this.hidePill()
  }

  // ── Measurement ──────────────────────────────────────────────────────────

  // Trailing-edge throttle: a scroll burst coalesces into one measure at most
  // every MEASURE_MIN_INTERVAL_MS. Deliberately NOT requestAnimationFrame —
  // rAF stops firing in hidden/occluded pages, which would wedge a "measure
  // in flight" flag and silently stop tracking for the rest of the visit.
  scheduleMeasure() {
    if (this.pendingMeasure) return
    const elapsed = performance.now() - this.lastMeasureAt
    this.pendingMeasure = setTimeout(() => {
      this.pendingMeasure = null
      this.lastMeasureAt = performance.now()
      this.measure()
    }, Math.max(0, MEASURE_MIN_INTERVAL_MS - elapsed))
  }

  measure() {
    const rect = this.proseTarget.getBoundingClientRect()
    if (rect.height <= 0) return

    const readBottom = window.innerHeight - rect.top
    this.percent = Math.round(Math.min(100, Math.max(0, (readBottom / rect.height) * 100)))
    this.blockIndex = this.topmostBlockIndex()

    if (this.saved && this.percent >= this.saved.pct) this.hidePill()
    if (!this.interacted) return // opening is not reading — track, don't save
    // Dedup against the last SAVED position (not the last measured one): a
    // position tracked before interaction — e.g. Chrome restoring the scroll
    // spot on reload — must still be saveable once the reader engages.
    if (this.positionKey() === this.lastSavedKey) return

    this.dirty = true
    if (!this.signedInValue && this.shouldPersist()) this.writeGuestEntry()
  }

  positionKey() {
    return `${this.percent}:${this.blockIndex}`
  }

  topmostBlockIndex() {
    for (let i = 0; i < this.blocks.length; i++) {
      if (this.blocks[i].getBoundingClientRect().bottom > TOP_BAR_OFFSET) return i
    }
    return this.blocks.length ? this.blocks.length - 1 : null
  }

  // ── Persistence ──────────────────────────────────────────────────────────

  // While the resume pill is still offering a saved spot, positions EARLIER
  // than it are not persisted: opening a chapter lands at the top, and that
  // must not clobber the real place the reader left off before they either
  // resume, dismiss (an explicit "I'm re-reading from here"), or catch up.
  shouldPersist() {
    return !(this.saved && this.percent < this.saved.pct)
  }

  persist() {
    // A throttled measure may lag the final scroll by up to the min interval —
    // capture the exact position before it leaves the page.
    if (this.blocks && this.blocks.length) this.measure()
    if (!this.dirty || !this.shouldPersist()) return
    if (this.signedInValue) {
      this.dirty = false
      this.lastSavedKey = this.positionKey()
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      fetch(this.urlValue, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token || '' },
        body: JSON.stringify({
          story_id: this.storyIdValue,
          chapter_id: this.chapterIdValue,
          percent: this.percent,
          block_index: this.blockIndex
        })
      }).catch(() => {})
    } else {
      this.writeGuestEntry()
      this.dirty = false
    }
  }

  savedPosition() {
    if (this.signedInValue) {
      if (this.resumePercentValue <= 0) return null
      return {
        pct: this.resumePercentValue,
        block: this.resumeBlockValue >= 0 ? this.resumeBlockValue : null
      }
    }
    const entry = this.readGuestEntry()
    if (!entry || entry.chapterId !== this.chapterIdValue) return null
    return { pct: entry.pct || 0, block: Number.isInteger(entry.block) ? entry.block : null }
  }

  readGuestEntry() {
    try {
      const raw = localStorage.getItem(STORE_PREFIX + this.storyIdValue)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }

  writeGuestEntry() {
    try {
      localStorage.setItem(
        STORE_PREFIX + this.storyIdValue,
        JSON.stringify({
          storyId: this.storyIdValue,
          chapterId: this.chapterIdValue,
          path: this.chapterPathValue,
          pos: this.chapterPositionValue,
          pct: this.percent,
          block: this.blockIndex,
          at: new Date().toISOString()
        })
      )
      this.lastSavedKey = this.positionKey()
    } catch {
      // localStorage unavailable/full — reading continues without a saved spot.
    }
  }

  // Keep guest storage bounded: newest MAX_GUEST_ENTRIES stories win.
  pruneGuestEntries() {
    try {
      const entries = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(STORE_PREFIX)) {
          const entry = JSON.parse(localStorage.getItem(key))
          entries.push({ key, at: entry?.at || '' })
        }
      }
      entries
        .sort((a, b) => (a.at < b.at ? 1 : -1))
        .slice(MAX_GUEST_ENTRIES)
        .forEach(({ key }) => localStorage.removeItem(key))
    } catch {
      // Best-effort housekeeping only.
    }
  }

  // ── Pill visibility ──────────────────────────────────────────────────────

  showPill() {
    if (this.hasPillTarget) this.pillTarget.hidden = false
  }

  hidePill() {
    if (this.hasPillTarget) this.pillTarget.hidden = true
    this.saved = null
  }
}
